import type { PartDraft } from '@/types/Part';

/**
 * 16 sample parts ported verbatim from public/legacy-demo.html.
 * Used by the "Seed Sample Parts" admin action to populate Firestore
 * on a fresh deploy. Prices in whole USD (see types/Part.ts note).
 */
export const SAMPLE_PARTS: readonly PartDraft[] = [
  {
    id: 'swc-001',
    sku: 'SWC-HEMI-HEAD-559',
    title: '1966 426 Hemi Cylinder Head Pair',
    year: 1966,
    category: 'engine-block',
    iconId: 'i-head',
    type: 'OEM',
    condition: 'Excellent',
    price: 8400,
    weight: 92,
    casting: '2780559',
    dateCode: 'A 12 6',
    fitment: ['1966 Plymouth Belvedere', '1966 Dodge Coronet', '1966 Plymouth Satellite'],
    description:
      'Matched pair of original 1966 Hemi cylinder heads. Date codes within 4 days of each other indicating likely matched-set production. No cracks, no welding repairs, no port work. Includes original valve guides.',
    photos: [],
    authentication: {
      verifiedBy: 'Kevin K.',
      verifiedDate: '2026-03-14',
      notes:
        'Casting numbers match factory production records. Original valve seats. Ports unmodified. Right head shows original blue paint trace under intake flange.',
    },
    stock: 1,
    status: 'live',
  },
  {
    id: 'swc-002',
    sku: 'SWC-HEMI-PISTON-426',
    title: '1968 426 Hemi Forged Pistons — Set of 8',
    year: 1968,
    category: 'engine-internal',
    iconId: 'i-piston',
    type: 'OEM',
    condition: 'NOS',
    price: 2950,
    weight: 18,
    casting: '2780960',
    dateCode: '8B14',
    fitment: ['1968 Charger R/T Hemi', '1968 Coronet R/T Hemi', '1968 GTX Hemi'],
    description:
      'Genuine factory forged pistons in original packaging. 10.25:1 compression. Includes original wrist pins.',
    photos: [],
    authentication: {
      verifiedBy: 'Kevin K.',
      verifiedDate: '2026-02-28',
      notes:
        'New old stock. Original Mopar Performance boxes. All 8 pistons in matched weight set ±2 grams. Skirts unmarked.',
    },
    stock: 1,
    status: 'live',
  },
  {
    id: 'swc-003',
    sku: 'SWC-HEMI-INTAKE-CR',
    title: '1970 426 Hemi Cross Ram Intake Manifold',
    year: 1970,
    category: 'induction',
    iconId: 'i-intake',
    type: 'OEM',
    condition: 'Excellent',
    price: 4800,
    weight: 64,
    casting: '2806178',
    dateCode: '0G27',
    fitment: ['1970 Cuda Hemi', '1970 Challenger R/T Hemi', '1970 Coronet Super Bee Hemi'],
    description:
      'Famous Hemi cross-ram dual quad intake manifold. Often the centerpiece of a Hemi engine show. Surface clean, no porosity issues.',
    photos: [],
    authentication: {
      verifiedBy: 'Kevin K.',
      verifiedDate: '2026-04-02',
      notes:
        'Casting numbers and date code confirmed against factory ledgers. No cracks at carb mounting bosses. Original aluminum, no media blasting damage.',
    },
    stock: 1,
    status: 'live',
  },
  {
    id: 'swc-004',
    sku: 'SWC-HEMI-VC-CHRM',
    title: '1969 426 Hemi Chrome Valve Covers (Pair)',
    year: 1969,
    category: 'valve-cover',
    iconId: 'i-cover',
    type: 'OEM',
    condition: 'Restored',
    price: 1450,
    weight: 14,
    casting: '2806674',
    dateCode: 'B22',
    fitment: ['1969 Charger R/T Hemi', '1969 Road Runner Hemi', '1969 GTX Hemi'],
    description:
      'The iconic chrome covers with embossed "Hemi" script. Completely restored to show-quality finish.',
    photos: [],
    authentication: {
      verifiedBy: 'Kevin K.',
      verifiedDate: '2026-01-18',
      notes:
        "Original cores, professionally re-chromed by Paul's Plating in 2024. Hemi script intact and crisp. Includes original rubber seals.",
    },
    stock: 1,
    status: 'live',
  },
  {
    id: 'swc-005',
    sku: 'SWC-HEMI-ROD-426',
    title: '1968 Hemi Connecting Rods — Set of 8',
    year: 1968,
    category: 'engine-internal',
    iconId: 'i-rod',
    type: 'OEM',
    condition: 'Excellent',
    price: 2200,
    weight: 28,
    casting: '2406770',
    dateCode: '8C09',
    fitment: ['1968 Hemi engines (all applications)'],
    description: 'Matched set of factory forged Hemi connecting rods, ready for assembly.',
    photos: [],
    authentication: {
      verifiedBy: 'Kevin K.',
      verifiedDate: '2026-03-22',
      notes:
        'All 8 rods Magnafluxed and verified crack-free. Matched within 4 grams. Original rod bolts replaced with ARP per industry restoration standard.',
    },
    stock: 1,
    status: 'live',
  },
  {
    id: 'swc-006',
    sku: 'SWC-HEMI-CRANK-68',
    title: '1968 426 Hemi Forged Crankshaft',
    year: 1968,
    category: 'engine-internal',
    iconId: 'i-crank',
    type: 'OEM',
    condition: 'Excellent',
    price: 3200,
    weight: 75,
    casting: '2780954',
    dateCode: '8E11',
    fitment: ['1968 Hemi engines (all applications)'],
    description:
      'Untouched original Hemi crankshaft on standard journals. Rare find for a true date-matched build.',
    photos: [],
    authentication: {
      verifiedBy: 'Kevin K.',
      verifiedDate: '2026-02-10',
      notes:
        'Original casting and forging marks intact. Standard journals — never reground. Verified straight on V-blocks within 0.0005".',
    },
    stock: 1,
    status: 'live',
  },
  {
    id: 'swc-007',
    sku: 'SWC-CUDA-HOOD-70',
    title: '1970 Plymouth Cuda Shaker Hood',
    year: 1970,
    category: 'body-sheet',
    iconId: 'i-hood',
    type: 'OEM',
    condition: 'Restorable',
    price: 5400,
    weight: 65,
    casting: 'NOS Stamping 2964114',
    dateCode: '0F03',
    fitment: ['1970 Plymouth Cuda', '1970 Plymouth AAR Cuda'],
    description:
      'Original sheet metal Cuda shaker hood. Surface rust only, solid metal underneath. Period-correct factory stamping.',
    photos: [],
    authentication: {
      verifiedBy: 'Kevin K.',
      verifiedDate: '2026-03-30',
      notes:
        'Original Cuda shaker hood with factory stamping marks. Two small dents on driver side, repairable. No rust-through. Center hole for shaker air grabber present.',
    },
    stock: 1,
    status: 'reserved',
  },
  {
    id: 'swc-008',
    sku: 'SWC-CHGR-TRUNK-69',
    title: '1969 Dodge Charger R/T Trunk Lid',
    year: 1969,
    category: 'body-sheet',
    iconId: 'i-trunk',
    type: 'OEM',
    condition: 'Good',
    price: 1800,
    weight: 42,
    casting: 'NOS Stamping 2925446',
    dateCode: '9A18',
    fitment: ['1968-70 Dodge Charger', '1968-70 Dodge Charger R/T'],
    description: 'Solid Charger trunk lid from a dry climate. Some surface scratches, no body damage.',
    photos: [],
    authentication: {
      verifiedBy: 'Kevin K.',
      verifiedDate: '2026-02-05',
      notes: 'California car, no rust. Original spot welds intact. Lock cylinder hole un-modified.',
    },
    stock: 1,
    status: 'live',
  },
  {
    id: 'swc-009',
    sku: 'SWC-RR-FENDER-69',
    title: '1969 Road Runner Front Fenders (Pair)',
    year: 1969,
    category: 'body-sheet',
    iconId: 'i-fender',
    type: 'OEM',
    condition: 'Excellent',
    price: 3600,
    weight: 38,
    casting: 'NOS Stamping',
    dateCode: '9C15',
    fitment: ['1968-70 Plymouth Road Runner', '1968-70 Plymouth GTX'],
    description:
      'A genuinely hard-to-find item. Most fenders this old need replacement panels — these are unmolested.',
    photos: [],
    authentication: {
      verifiedBy: 'Kevin K.',
      verifiedDate: '2026-04-10',
      notes:
        'Solid pair of NOS-quality fenders pulled from a low-mile Texas car. No filler, no patches. Stamping marks photographed.',
    },
    stock: 1,
    status: 'live',
  },
  {
    id: 'swc-010',
    sku: 'SWC-HEMI-BLOCK-70',
    title: '1970 426 Hemi Engine Block',
    year: 1970,
    category: 'engine-block',
    iconId: 'i-block',
    type: 'OEM',
    condition: 'Excellent',
    price: 12500,
    weight: 240,
    casting: '2468330',
    dateCode: '0D04',
    fitment: ['1970 Hemi engines (all applications)'],
    description:
      'The foundation of a real Hemi build. Numbers-matching casting on a date-coded block from April 1970. Documented.',
    photos: [],
    authentication: {
      verifiedBy: 'Kevin K.',
      verifiedDate: '2026-03-01',
      notes:
        'Casting numbers and date code verified. Block has been pressure-tested — no cracks. Bored 0.030" over from previous owner. All freeze plugs replaced. Ready for assembly.',
    },
    stock: 1,
    status: 'live',
  },
  {
    id: 'swc-011',
    sku: 'SWC-HEMI-CAM-68',
    title: '1968 426 Hemi Cam & Lifter Set',
    year: 1968,
    category: 'engine-internal',
    iconId: 'i-cam',
    type: 'OEM',
    condition: 'NOS',
    price: 1200,
    weight: 12,
    casting: '2780795',
    dateCode: '8H21',
    fitment: ['1968-70 Hemi engines'],
    description:
      'Factory replacement cam and lifters, never installed. The matched lifter set is what makes this rare.',
    photos: [],
    authentication: {
      verifiedBy: 'Kevin K.',
      verifiedDate: '2026-04-18',
      notes:
        'New old stock cam and matched lifter set in original Mopar Performance boxes. Lobe profile within factory spec.',
    },
    stock: 1,
    status: 'live',
  },
  {
    id: 'swc-012',
    sku: 'SWC-HEMI-CARB-DQ',
    title: '1968 Hemi Carter AFB Dual Quad Carbs',
    year: 1968,
    category: 'induction',
    iconId: 'i-carb',
    type: 'OEM',
    condition: 'Restored',
    price: 3850,
    weight: 22,
    casting: 'AFB 4324S / 4325S',
    dateCode: '8 09',
    fitment: ['1966-71 Hemi Cross Ram applications'],
    description:
      'The Carter AFBs that sat on top of every cross-ram Hemi. Front and rear matched, rebuilt to working condition.',
    photos: [],
    authentication: {
      verifiedBy: 'Kevin K.',
      verifiedDate: '2026-01-28',
      notes:
        'Matched front/rear pair with sequential serial numbers. Professionally rebuilt by Carbs Unlimited (Hemmings #117). New gaskets, accelerator pumps, and bowl floats. Bench-tested at 6.5 PSI.',
    },
    stock: 1,
    status: 'live',
  },
  {
    id: 'swc-013',
    sku: 'SWC-HEMI-EXH-MAN',
    title: '1969 Hemi Exhaust Manifolds (Pair)',
    year: 1969,
    category: 'exhaust',
    iconId: 'i-exhaust',
    type: 'OEM',
    condition: 'Good',
    price: 1350,
    weight: 48,
    casting: '2780751 / 2780752',
    dateCode: '9D11',
    fitment: ['1968-71 B-body Hemi', '1970-71 E-body Hemi'],
    description:
      'Hard-to-find original Hemi exhaust manifolds. Many of these get cracked under heat cycling — these are sound.',
    photos: [],
    authentication: {
      verifiedBy: 'Kevin K.',
      verifiedDate: '2026-02-14',
      notes:
        'Casting numbers and date codes verified. No cracks at flange. Light surface rust, originals never coated. Good cores for ceramic coating.',
    },
    stock: 2,
    status: 'live',
  },
  {
    id: 'swc-014',
    sku: 'SWC-CUDA-INTERIOR',
    title: '1971 Cuda Door Panels & Console',
    year: 1971,
    category: 'interior',
    iconId: 'i-interior',
    type: 'OEM',
    condition: 'Restored',
    price: 2400,
    weight: 35,
    casting: 'Trim Code H6X9',
    dateCode: '1B',
    fitment: ['1971 Plymouth Cuda', '1971 Plymouth Cuda 340/383/440/Hemi'],
    description:
      'Door panel and floor console restoration. Hard to source these together as a matched pair of components.',
    photos: [],
    authentication: {
      verifiedBy: 'Kevin K.',
      verifiedDate: '2026-03-18',
      notes:
        'Original door panel boards re-skinned with correct grain vinyl by Legendary Auto Interiors. Console restored with new wood grain insert (factory-correct supplier).',
    },
    stock: 1,
    status: 'live',
  },
  {
    id: 'swc-015',
    sku: 'SWC-DAYTONA-WING',
    title: '1969 Charger Daytona Wing Stanchions',
    year: 1969,
    category: 'body-sheet',
    iconId: 'i-wing',
    type: 'OEM',
    condition: 'Good',
    price: 6800,
    weight: 18,
    casting: 'NOS Stamping',
    dateCode: '9G02',
    fitment: ['1969 Dodge Charger Daytona'],
    description:
      'Original tower stanchions for the legendary Charger Daytona. Documented provenance. The wing element is sold separately.',
    photos: [],
    authentication: {
      verifiedBy: 'Kevin K.',
      verifiedDate: '2026-02-22',
      notes:
        'Original Daytona wing stanchions — only 503 cars built. Provenance documented from previous owner (third owner, retains 1972 sale receipt). Mounting holes un-modified.',
    },
    stock: 1,
    status: 'live',
  },
  {
    id: 'swc-016',
    sku: 'SWC-HEMI-BOLTS-HD',
    title: 'Hemi Cylinder Head Bolt Set — NOS',
    year: 1968,
    category: 'fasteners',
    iconId: 'i-fastener',
    type: 'OEM',
    condition: 'NOS',
    price: 425,
    weight: 4,
    casting: 'P/N 2406341',
    dateCode: '8F12',
    fitment: ['1965-71 426 Hemi engines'],
    description:
      'Often overlooked but critical for a date-correct restoration. Period-correct head markings.',
    photos: [],
    authentication: {
      verifiedBy: 'Kevin K.',
      verifiedDate: '2026-04-21',
      notes:
        'New old stock head bolt set in original Mopar Performance package. Plating and hex marks correct for date code.',
    },
    stock: 3,
    status: 'live',
  },
];
